import { Point, Position, ControlPoint } from '../utils/types';

// Calculate a point on a cubic Bezier curve
function cubicBezier(p0: ControlPoint, p1: ControlPoint, p2: ControlPoint, p3: ControlPoint, t: number): { x: number; y: number } {
  const cx = 3 * (p1.x - p0.x);
  const bx = 3 * (p2.x - p1.x) - cx;
  const ax = p3.x - p0.x - cx - bx;

  const cy = 3 * (p1.y - p0.y);
  const by = 3 * (p2.y - p1.y) - cy;
  const ay = p3.y - p0.y - cy - by;

  const x = ax * Math.pow(t, 3) + bx * Math.pow(t, 2) + cx * t + p0.x;
  const y = ay * Math.pow(t, 3) + by * Math.pow(t, 2) + cy * t + p0.y;

  return { x, y };
}

// Calculate the angle between two points
function calculateAngle(p1: ControlPoint, p2: ControlPoint): number {
  const angleRad = Math.atan2(p2.y - p1.y, p2.x - p1.x); // In radians, -PI to PI
  let angleDeg = angleRad * (180 / Math.PI); // Convert to degrees, -180 to 180

  // Normalize to be between 0 and 360 degrees
  if (angleDeg < 0) {
    angleDeg += 360;
  }
  
  // console.log(`[calculateAngle] p1: (${p1.x.toFixed(2)},${p1.y.toFixed(2)}) p2: (${p2.x.toFixed(2)},${p2.y.toFixed(2)}) raw angle (0-360): ${angleDeg.toFixed(2)}`);
  return angleDeg;
}

export function calculatePath(controlPoints: ControlPoint[], duration: number): Point[] {
  const points: Point[] = [];
  const numPoints = Math.ceil(duration / 10); // Increased density for smoother rotation

  for (let i = 0; i < numPoints; i++) {
    const t = i / (numPoints - 1);
    const point = cubicBezier(
      controlPoints[0],
      controlPoints[1],
      controlPoints[2],
      controlPoints[3],
      t
    );

    // Calculate rotation based on the next point
    let rotation = 0;
    if (i < numPoints - 1) {
      const nextPoint = cubicBezier(
        controlPoints[0],
        controlPoints[1],
        controlPoints[2],
        controlPoints[3],
        (i + 1) / (numPoints - 1)
      );
      rotation = calculateAngle(point, nextPoint);
    } else {
      // For the very last point, use the rotation of the previous point for smooth transition
      if (points.length > 0) {
        rotation = points[points.length - 1].rotation;
      }
    }

    points.push({
      x: point.x,
      y: point.y,
      rotation,
      time: (i / (numPoints - 1)) * duration
    });
  }

  return points;
}

// Calculate bee position along a path
export function calculateBeePosition(
  points: Point[],
  elapsed: number,
  totalDuration: number,
  canvasWidth: number = 1,
  canvasHeight: number = 1
): Position {
  // Find the two points to interpolate between
  let i = 0;
  while (i < points.length - 1 && points[i + 1].time < elapsed) {
    i++;
  }

  if (i >= points.length - 1) {
    const lastPoint = points[points.length - 1];
    // Check if points are already normalized (x and y are between 0 and 1)
    const isNormalized = lastPoint.x <= 1 && lastPoint.y <= 1 && lastPoint.x >= 0 && lastPoint.y >= 0;

    // console.log(`[calculateBeePosition] At end of path. Last point: x=${lastPoint.x.toFixed(2)}, y=${lastPoint.y.toFixed(2)}, rotation=${lastPoint.rotation.toFixed(2)}, time=${lastPoint.time.toFixed(0)}. isNormalized=${isNormalized}, canvasWidth=${canvasWidth}, canvasHeight=${canvasHeight}`);

    return {
      x: isNormalized ? lastPoint.x * canvasWidth : lastPoint.x,
      y: isNormalized ? lastPoint.y * canvasHeight : lastPoint.y,
      rotation: lastPoint.rotation
    };
  }

  const p1 = points[i];
  const p2 = points[i + 1];
  const t = (elapsed - p1.time) / (p2.time - p1.time);

  // Check if points are already normalized
  const isNormalized = p1.x <= 1 && p1.y <= 1 && p2.x <= 1 && p2.y <= 1 && p1.x >= 0 && p1.y >= 0 && p2.x >= 0 && p2.y >= 0;

  // Interpolate position
  const x = isNormalized 
    ? (p1.x + (p2.x - p1.x) * t) * canvasWidth
    : p1.x + (p2.x - p1.x) * t;
  const y = isNormalized
    ? (p1.y + (p2.y - p1.y) * t) * canvasHeight
    : p1.y + (p2.y - p1.y) * t;

  // Interpolate rotation, taking the shortest path
  let deltaRotation = p2.rotation - p1.rotation;
  if (deltaRotation > 180) {
    deltaRotation -= 360;
  } else if (deltaRotation < -180) {
    deltaRotation += 360;
  }
  let rotation = p1.rotation + deltaRotation * t;
  
  // console.log(`[calculateBeePosition] Interpolating: elapsed=${elapsed.toFixed(0)}, i=${i}, p1=(r:${p1.rotation.toFixed(2)}), p2=(r:${p2.rotation.toFixed(2)}), t_interp=${t.toFixed(2)}, calculated_rotation=${rotation.toFixed(2)})`);

  return { x, y, rotation };
}

export const shouldEndAnimation = (elapsed: number, totalDuration: number): boolean => {
  return elapsed >= totalDuration;
}; 